// Marca visible del NeoCore.
// - Producción: Neocoop (naranja).
// - Demo (VITE_DEMO=1): la cooperativa definida en admin/tenants/<VITE_TENANT>.json
//   (logos + colores). Cada prospecto de Nechy tiene su archivo de tenant.
export const IS_DEMO = import.meta.env.VITE_DEMO === '1'

export type Tenant = {
  slug: string
  nombre: string
  sub: string
  razon_social?: string
  colores: { primario: string; acento: string; sobre_primario?: string; primario_texto?: string }
  assets: {
    logo_color: string; logo_blanco: string
    iso_primario: string; iso_acento: string; iso_blanco: string; favicon: string
    marca_tile?: string
  }
}

const TENANTS = import.meta.glob<Tenant>('../../tenants/*.json', { eager: true, import: 'default' })
const slug = (import.meta.env.VITE_TENANT as string | undefined) || 'gente-solidaria'
export const TENANT: Tenant | null = IS_DEMO ? (TENANTS[`../../tenants/${slug}.json`] ?? null) : null

export const BRAND = TENANT
  ? {
      nombre: TENANT.nombre,
      sub: TENANT.sub,
      isoClaro: TENANT.assets.iso_primario,
      isoOscuro: TENANT.assets.iso_blanco,
      isoSobreColor: TENANT.assets.iso_blanco,
      logo: TENANT.assets.logo_color,
      logoSobreColor: TENANT.assets.logo_blanco,
      isoAcento: TENANT.assets.iso_acento,
      tile: TENANT.assets.marca_tile ?? TENANT.assets.favicon,
      titulo: `NeoCore · ${TENANT.nombre}`,
    }
  : {
      nombre: 'neocoop.',
      sub: 'NeoCore',
      isoClaro: '/brand/isotipo-naranja.svg',
      isoOscuro: '/brand/isotipo-blanco.svg',
      isoSobreColor: '/brand/isotipo-blanco.svg',
      logo: '/brand/logo-horizontal.png',
      logoSobreColor: '/brand/logo-horizontal.png',
      isoAcento: '/brand/isotipo-naranja.svg',
      tile: '/brand/icon.png',
      titulo: 'NeoCore',
    }

// ── Paleta derivada del color primario del tenant (escala 50…900) ──────────────
function mezclar(hex: string, con: string, t: number): string {
  const a = hex.replace('#', ''), b = con.replace('#', '')
  const ch = (s: string, i: number) => parseInt(s.slice(i, i + 2), 16)
  const m = (i: number) => Math.round(ch(a, i) + (ch(b, i) - ch(a, i)) * t).toString(16).padStart(2, '0')
  return `#${m(0)}${m(2)}${m(4)}`
}

export function aplicarTemaTenant(): void {
  if (!TENANT) return
  const c = TENANT.colores
  const claro = c.primario                       // color de marca (superficies, botones, gráficas)
  const p = c.primario_texto || c.primario       // versión legible sobre blanco (texto, legacy)
  const tinta = c.sobre_primario || '#ffffff'    // texto encima del primario
  const r = document.documentElement
  const v: Record<string, string> = {
    '--brand': p, '--brand-deep': mezclar(p, '#000000', 0.3), '--brand-soft': mezclar(claro, '#ffffff', 0.84),
    '--brand-50': mezclar(claro, '#ffffff', 0.92), '--brand-100': mezclar(claro, '#ffffff', 0.84),
    '--brand-200': mezclar(claro, '#ffffff', 0.68), '--brand-300': mezclar(claro, '#ffffff', 0.45),
    '--brand-400': mezclar(claro, '#ffffff', 0.15), '--brand-500': p,
    '--brand-600': mezclar(p, '#000000', 0.12), '--brand-700': mezclar(p, '#000000', 0.3),
    '--brand-800': mezclar(p, '#000000', 0.5), '--brand-900': mezclar(p, '#000000', 0.7),
    '--primary': claro, '--primary-foreground': tinta, '--ring': p,
    '--chart-1': claro, '--chart-2': c.acento, '--chart-3': p,
    // Sidebar en el color de la marca, letras y logo en blanco
    '--sidebar': claro, '--sidebar-foreground': '#ffffff',
    '--sidebar-accent': 'rgba(255,255,255,0.22)', '--sidebar-accent-foreground': '#ffffff',
    '--sidebar-border': 'rgba(255,255,255,0.28)',
    '--sidebar-primary': c.acento, '--sidebar-primary-foreground': '#ffffff', '--sidebar-ring': '#ffffff',
    '--accent-tenant': c.acento,
    '--tenant-primario': claro, '--tenant-acento': c.acento, '--tenant-tinta': tinta, '--tenant-texto': p,
  }
  for (const [k, val] of Object.entries(v)) r.style.setProperty(k, val)
  r.classList.add('demo-brand')
  document.title = BRAND.titulo
  const fav = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (fav) fav.href = TENANT.assets.favicon
}
